import React, { useState, useEffect, useRef, useCallback } from "react";
import { Modal, Button, Form, Row, Col, Container } from "react-bootstrap";
import { getLMSEnrollments, selectUser } from "../../Redux/Actions/enrollmentsAction";
import "bootstrap/dist/css/bootstrap.css";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import "../../config";
import "react-toastify/dist/ReactToastify.css";
import ManageCourseContainer from "./manageCourseContainer";
import { formatActivitySession } from "../../SelectRoster/components/courseOfferingActivityModal";
import FormattedDuration from "../../utils/FormattedDuration";
import { MULTI_CLASS_SELECT_LEARNERS_KEY } from "../../constants";
import CommonModal from "../../components/CommonModal";
import FindLearnerModal from "../components/FindLearnerModal";
import "./manage-course-container.css";

/** Workers REST / UX enrollments use `hcmDlStatus` for pipeline state. */
const ENROLLMENT_SUCCESS = "enrollment-success";
const COMPLETION_SUCCESS = "completion-success";

const isLearnerPollingSettled = (e) =>
  e?.hcmDlStatus === COMPLETION_SUCCESS || e?.hcmDlStatus === ENROLLMENT_SUCCESS;

/** Skip aggressive polling when every known learner is enrolled or completed (empty roster never skips). */
const shouldSkipEnrollmentPoll = (enrollments) =>
  Array.isArray(enrollments) &&
  enrollments.length > 0 &&
  enrollments.every(isLearnerPollingSettled);

// ORACLEARN ROSTER MANAGEMENT
const ManageOfferingActivityRoster = () => {
  const [name, setName] = useState("");

  const [enrollmentsLoading, setEnrollmentsLoading] = useState(true);

  const [courseNumber, setCourseNumber] = useState("");
  const [courseName, setCourseName] = useState("");
  const [courseId, setCourseId] = useState("");

  const [offeringNumber, setOfferingNumber] = useState("");
  const [offeringName, setOfferingName] = useState("");
  const [offeringId, setOfferingId] = useState("");
  const [offeringStartDate, setOfferingStartDate] = useState("");
  const [offeringEndDate, setOfferingEndDate] = useState("");

  const [activityName, setActivityName] = useState("");
  const [activityNumber, setActivityNumber] = useState("");
  const [activityId, setActivityId] = useState("");
  const [activityVenue, setActivityVenue] = useState("");
  const [activityTime, setActivityTime] = useState("");

  const [showAlert, setShowAlert] = useState({
    userNotFound: false,
  });

  const [isLoading, setIsLoading] = useState(false); // this has been added to replace [loadingLearners, setLoadingLearners] and [usersLoading, setUsersLoading], and [enrollmentsLoading, setEnrollmentsLoading], phase out those states out when <ManageCourseContainer is stable and old code is no longer needed.

  //let baseApiUrl = "http://rldoraclelearn.localhost:8080/v1";
  let baseApiUrl = global.config.baseApiUrl;
  const companyId = global.config.companyId;

  const {
    course: currentCourse,
    session: currentSession,
    event: currentEvent,
  } = useSelector((state) => state.courses);
  const { user: currentUser } = useSelector((state) => state.auth);
  const { enrollments: currentEnrollments } = useSelector((state) => state.enrollments);

  const dispatch = useDispatch();

  const multiClassAutoAddInFlightRef = useRef(null);
  const enrollmentsRef = useRef(currentEnrollments);
  enrollmentsRef.current = currentEnrollments;
  const lastEnrollmentFetchAtRef = useRef(0);

  const [show, setShow] = useState(false);
  const [refresh, setRefresh] = useState(false);

  const CONFIRM_PREF_KEY = "rapidRoster:disableLearnerConfirmPrompt";
  const [pendingLearner, setPendingLearner] = useState(null); // { user, learnerKey }
  const [showConfirmLearner, setShowConfirmLearner] = useState(false);
  const [dontPromptConfirmations, setDontPromptConfirmations] = useState(() => {
    try {
      return sessionStorage.getItem(CONFIRM_PREF_KEY) === "true";
    } catch {
      return false;
    }
  });

  const { activitySession, startDate, endDate } = formatActivitySession(
    offeringStartDate,
    activityTime,
  );

  const courseDetails = [
    { key: "Course", value: courseName },
    {
      key: "Session",
      value: activitySession,
    },
    { key: "Offering Number", value: offeringNumber },
    { key: "Start Date", value: startDate },
    { key: "End Date", value: endDate },
    { key: "Location", value: activityVenue },
    { key: "Duration", value: <FormattedDuration timeRange={activityTime} /> },
  ];

  const onChangeName = (e) => {
    const name = e.target.value;
    setName(name);
  };

  const handleOpen = () => {
    setShow(true);
  };

  const handleFindLearnerClose = () => {
    dispatch(selectUser(null));
    setShow(false);
  };

  const buildRosterRequestBody = (learners) => {
    const { course_number, course_name, course_id } = getCourseFields();
    const { offering_number, offering_name, offering_id, offering_start_date, offering_end_date } =
      getSessionFields();
    const { activity_number, activity_name, activity_id, activity_venue, activity_time } =
      getEventFields();

    setOfferingNumber(offering_number);
    setActivityNumber(activity_number);

    setCourseName(course_name);
    setActivityName(activity_name);

    setOfferingStartDate(offering_start_date);
    setOfferingEndDate(offering_end_date);

    setActivityVenue(activity_venue);
    setActivityTime(activity_time);

    return {
      courseId: course_id,
      courseTitle: course_name,
      courseNumber: course_number,
      offeringId: offering_id,
      offeringTitle: offering_name,
      offeringNumber: offering_number,
      activityId: activity_id,
      activityTitle: activity_name,
      activityNumber: activity_number,
      activityVenue: activity_venue,
      activityTime: activity_time,
      offeringStartDate: offering_start_date,
      offeringEndDate: offering_end_date,
      instructorNumber: currentUser.id,
      instructorName: currentUser.instructorname,
      retrieveEnrollmentsRealtime: false,
      realtimeEnrollmentsData: currentEnrollments?.data?.enrollments_from_rt,
      learners,
    };
  };

  const submitLearnersToRoster = async (learners, rosterOpts = {}) => {
    if (!Array.isArray(learners) || learners.length === 0) return;

    setEnrollmentsLoading(true);
    try {
      const reqBody = buildRosterRequestBody(learners);
      const data = await axios.post(`${baseApiUrl}/learner/check`, reqBody);
      dispatch(getLMSEnrollments(data, false));
      lastEnrollmentFetchAtRef.current = Date.now();
      if (rosterOpts.clearMultiClassCacheOnSuccess) {
        try {
          sessionStorage.removeItem(MULTI_CLASS_SELECT_LEARNERS_KEY);
        } catch {
          // ignore
        }
      }
    } catch (error) {
      // the backend throws this when a learner element is not a string
      if (error?.message === "userIds[l].split is not a function") {
        enableAlert("userNotFound");
      } else {
        console.error(error);
      }
    } finally {
      setEnrollmentsLoading(false);
    }
  };

  // ─── Multi-class-select: auto-add learners after picking new session ──────────
  useEffect(() => {
    const hasSelection = Boolean(currentCourse && currentSession && currentEvent);
    if (!hasSelection) return;

    let learners;
    try {
      learners = JSON.parse(sessionStorage.getItem(MULTI_CLASS_SELECT_LEARNERS_KEY) || "[]");
    } catch {
      learners = [];
    }

    if (!Array.isArray(learners) || learners.length === 0) return;
    if (multiClassAutoAddInFlightRef.current) return;

    const p = submitLearnersToRoster(learners, {
      clearMultiClassCacheOnSuccess: true,
    });
    multiClassAutoAddInFlightRef.current = p;
    p.finally(() => {
      if (multiClassAutoAddInFlightRef.current === p) {
        multiClassAutoAddInFlightRef.current = null;
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCourse, currentSession, currentEvent]);

  // ─── Session storage helpers ──────────────────────────────────────────────────
  const getCourseFields = () => ({
    course_number: currentCourse?.course_number ?? sessionStorage.getItem("course_number") ?? "",
    course_name: currentCourse?.course_name ?? sessionStorage.getItem("course_name") ?? "",
    course_id: currentCourse?.course_id ?? sessionStorage.getItem("course_id") ?? "",
  });

  const getSessionFields = () => ({
    offering_number:
      currentSession?.offering_number ?? sessionStorage.getItem("offering_number") ?? "",
    offering_name: currentSession?.offering_name ?? sessionStorage.getItem("offering_name") ?? "",
    offering_id: currentSession?.offering_id ?? sessionStorage.getItem("offering_id") ?? "",
    offering_start_date:
      currentSession?.offering_start_date ?? sessionStorage.getItem("offering_start_date") ?? "",
    offering_end_date:
      currentSession?.offering_end_date ?? sessionStorage.getItem("offering_end_date") ?? "",
  });

  const getEventFields = () => ({
    activity_number:
      currentEvent?.activity_number ?? sessionStorage.getItem("activity_number") ?? "",
    activity_name: currentEvent?.activity_name ?? sessionStorage.getItem("activity_name") ?? "",
    activity_id: currentEvent?.activity_id ?? sessionStorage.getItem("activity_id") ?? "",
    activity_venue: currentEvent?.activity_venue ?? sessionStorage.getItem("activity_venue") ?? "",
    activity_time: currentEvent?.activity_time ?? sessionStorage.getItem("activity_time") ?? "",
  });

  async function enrollUser(e) {
    if (!name) return;

    const keyCode = e?.keyCode;

    // Check for common barcode scanner keycodes
    const scannerKeyCodes = [13, 9]; // Enter, Tab

    let scanned = "N";

    if (scannerKeyCodes.includes(keyCode)) {
      // Likely a barcode scanner input
      // Perform actions with the scanned data
      scanned = "Y";
    } else {
      // Regular keyboard input
      scanned = "N";
    }
    //e.preventDefault();
    // Set the UserId in the Redux
    // Check-In Learner | Mark Attendance | Completion | Walk-In Learner
    // Check Learner if in DB, also check the enrollment listings
    // Walk-In,
    // Check-In
    // Mark Attendance
    // Mark Completion

    try {
      const res = await axios.post(`${baseApiUrl}/people`, { search_text: name, scanned });
      // if there is more than one user or no users are found, then the username entered is invalid
      const users = res?.data?.users;
      if (!users || users.length === 0 || users.length >= 2) {
        enableAlert("userNotFound");
        return;
      }

      const user = users[0];
      const learnerName = `${user.user_id}:${user.first_name} ${user.last_name}`;

      if (dontPromptConfirmations) {
        setName("");
        await submitLearnersToRoster([learnerName]);
        return;
      }

      setPendingLearner({ user, learnerKey: learnerName });
      setShowConfirmLearner(true);
    } catch (error) {
      enableAlert("userNotFound");
    }
  }

  const handleCancelConfirmLearner = () => {
    setShowConfirmLearner(false);
    setPendingLearner(null);
  };

  const handleApproveConfirmLearner = async () => {
    if (!pendingLearner?.learnerKey) return;
    setShowConfirmLearner(false);
    setName("");

    localStorage.setItem(CONFIRM_PREF_KEY, String(dontPromptConfirmations));

    await submitLearnersToRoster([pendingLearner.learnerKey]);
    setPendingLearner(null);
  };

  const handleFindLearnerConfirm = async (learnerKey) => {
    if (!learnerKey) return;
    await submitLearnersToRoster([learnerKey]);
  };

  const confirmFields = (() => {
    const u = pendingLearner?.user;
    if (!u) return [];

    if (companyId?.includes("ford")) {
      return [
        { label: "GPID (Person Number)", value: u.user_id ?? "N/A" },
        { label: "CDSID (Username)", value: u.username ?? "N/A" },
        { label: "Email", value: u.email ?? "N/A" },
      ];
    }

    return [
      { label: "User Id", value: u.user_id ?? "N/A" },
      { label: "Username", value: u.username ?? "N/A" },
      { label: "Email", value: u.email ?? "N/A" },
      { label: "Manager", value: u.is_manager ? "Yes" : "No" },
    ];
  })();

  useEffect(() => {
    if (!currentCourse) return;
    const { course_number, course_name, course_id } = getCourseFields();
    setCourseNumber(course_number);
    setCourseName(course_name);
    setCourseId(course_id);
    sessionStorage.setItem("course_number", course_number);
    sessionStorage.setItem("course_name", course_name);
    sessionStorage.setItem("course_id", course_id);
  }, [currentCourse]);

  useEffect(() => {
    if (!currentSession) return;
    const { offering_number, offering_name, offering_id, offering_start_date, offering_end_date } =
      getSessionFields();
    setOfferingNumber(offering_number);
    setOfferingName(offering_name);
    setOfferingId(offering_id);
    setOfferingStartDate(offering_start_date);
    setOfferingEndDate(offering_end_date);
    sessionStorage.setItem("offering_number", offering_number);
    sessionStorage.setItem("offering_name", offering_name);
    sessionStorage.setItem("offering_id", offering_id);
    sessionStorage.setItem("offering_start_date", offering_start_date);
    sessionStorage.setItem("offering_end_date", offering_end_date);
  }, [currentSession]);

  useEffect(() => {
    if (!currentEvent) return;
    const { activity_number, activity_name, activity_id, activity_venue, activity_time } =
      getEventFields();
    setActivityNumber(activity_number);
    setActivityName(activity_name);
    setActivityId(activity_id);
    setActivityVenue(activity_venue);
    setActivityTime(activity_time);
    sessionStorage.setItem("activity_number", activity_number);
    sessionStorage.setItem("activity_name", activity_name);
    sessionStorage.setItem("activity_id", activity_id);
    sessionStorage.setItem("activity_venue", activity_venue);
    sessionStorage.setItem("activity_time", activity_time);
  }, [currentEvent]);

  // Get Enrollment Listings
  useEffect(() => {
    let unmounted = false;

    async function getOraclelearnEnrollments() {
      setEnrollmentsLoading(true);

      const { course_number, course_name } = getCourseFields();
      const { offering_number, offering_name } = getSessionFields();
      const { activity_number, activity_name } = getEventFields();

      setCourseNumber(course_number);
      setOfferingNumber(offering_number);
      setActivityNumber(activity_number);
      setCourseName(course_name);
      setOfferingName(offering_name);
      setActivityName(activity_name);

      sessionStorage.setItem("instructor_number", currentUser.id);
      sessionStorage.setItem("instructor_name", currentUser.instructorname);

      const reqBody = {
        courseNumber: course_number,
        offeringNumber: offering_number,
        activityNumber: activity_number,
        offeringStartDate,
        offeringEndDate,
        instructorNumber: currentUser.id,
        instructorName: currentUser.instructorname,
        retrieveEnrollmentsRealtime: true,
      };

      const data = await axios.post(`${baseApiUrl}/enrollments`, reqBody);
      if (data) {
        dispatch(getLMSEnrollments(data, false));
        lastEnrollmentFetchAtRef.current = Date.now();
      }

      if (!unmounted) {
        setEnrollmentsLoading(false);
      }
    }

    getOraclelearnEnrollments();

    return () => {
      unmounted = true;
    };
  }, [refresh]);

  const [refreshInterval] = useState(300000); // 300 sec — 5 min

  /**
   * Poll enrollments. When `force` is false, skips the API if every learner is already
   * enrollment-success or completion-success (reduces load). Use `force` after tab becomes
   * visible or for periodic discovery while the roster looks “settled” so new enrollments still appear.
   */
  const refreshEnrollments = useCallback(
    async (options = {}) => {
      const { force = false } = options;

      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        return;
      }

      const enrollments = enrollmentsRef.current?.data?.enrollments;
      if (!force && shouldSkipEnrollmentPoll(enrollments)) {
        return;
      }

      const { course_number, course_name } = getCourseFields();
      const { offering_number, offering_name } = getSessionFields();
      const { activity_number, activity_name } = getEventFields();

      setCourseNumber(course_number);
      setOfferingNumber(offering_number);
      setActivityNumber(activity_number);
      setCourseName(course_name);
      setOfferingName(offering_name);
      setActivityName(activity_name);

      const reqBody = {
        courseNumber: course_number,
        offeringNumber: offering_number,
        activityNumber: activity_number,
        offeringStartDate,
        offeringEndDate,
        instructorNumber: currentUser.id,
        instructorName: currentUser.instructorname,
        retrieveEnrollmentsRealtime: true,
      };

      const data = await axios.post(`${baseApiUrl}/enrollments`, reqBody);

      if (data) {
        dispatch(getLMSEnrollments(data, false));
        lastEnrollmentFetchAtRef.current = Date.now();
      }
    },
    [
      baseApiUrl,
      offeringEndDate,
      offeringStartDate,
      currentUser.id,
      currentUser.instructorname,
      currentCourse,
      currentSession,
      currentEvent,
    ],
  );

  /** While roster is “settled”, still discover new learners at a lower cadence (every N × poll interval). */
  const settledDiscoveryMultiplier = 4;

  useEffect(() => {
    if (!refreshInterval || refreshInterval <= 0) return;

    const tick = () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        return;
      }
      const enrollments = enrollmentsRef.current?.data?.enrollments;
      if (!shouldSkipEnrollmentPoll(enrollments)) {
        refreshEnrollments();
        return;
      }
      const elapsed = Date.now() - lastEnrollmentFetchAtRef.current;
      if (elapsed >= refreshInterval * settledDiscoveryMultiplier) {
        refreshEnrollments({ force: true });
      }
    };

    const interval = setInterval(tick, refreshInterval);
    return () => clearInterval(interval);
  }, [refreshEnrollments, refreshInterval]);

  /** When tab becomes visible again, refresh if we have not fetched recently (catches new learners after idle tab). */
  useEffect(() => {
    const onVisibilityChange = () => {
      if (typeof document === "undefined" || document.visibilityState !== "visible") {
        return;
      }
      const elapsed = Date.now() - lastEnrollmentFetchAtRef.current;
      if (elapsed >= refreshInterval) {
        refreshEnrollments();
      }
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [refreshEnrollments, refreshInterval]);

  const toggleAlertState = (obj, bool) => {
    setShowAlert((prevState) => ({
      ...prevState,
      [obj]: bool,
    }));
  };

  const hideAlert = (obj) => {
    toggleAlertState(obj, false);
  };

  const enableAlert = (obj) => {
    toggleAlertState(obj, true);
  };

  return (
    <>
      <ManageCourseContainer
        loadingState={{
          isLoading: isLoading || enrollmentsLoading,
          setIsLoading,
          enrollmentsLoading: enrollmentsLoading,
        }}
        courseDetails={courseDetails}
        showGearCancelCheckIn={!companyId?.includes("ford")}
        search={{
          term: name,
          onChangeTerm: onChangeName,
          onFindLearner: handleOpen,
          onConfirm: enrollUser,
        }}
      />

      <FindLearnerModal
        show={show}
        onClose={handleFindLearnerClose}
        onConfirm={handleFindLearnerConfirm}
      />

      <CommonModal
        show={showConfirmLearner}
        onHide={handleCancelConfirmLearner}
        title={<Modal.Title>Confirm Learner</Modal.Title>}
        footer={
          <div className="d-flex justify-content-between align-items-center w-100 px-2">
            <Form.Check
              type="checkbox"
              id="dont-prompt-confirmations"
              label="Don't prompt for confirmations"
              className="field-label"
              checked={dontPromptConfirmations}
              onChange={(e) => setDontPromptConfirmations(e.target.checked)}
            />
            <div className="d-flex" style={{ gap: "10px" }}>
              <Button variant="dark" onClick={handleCancelConfirmLearner}>
                Cancel
              </Button>
              <Button variant="success" onClick={handleApproveConfirmLearner}>
                Approve
              </Button>
            </div>
          </div>
        }
        modalProps={{
          centered: true,
          backdrop: "static",
          className: "manage-roster-modal confirm-learner-modal",
          size: "lg",
        }}
      >
        <Container>
          <Row className="align-items-center">
            <Col xs="auto">
              <div
                style={{
                  width: 120,
                  height: 120,
                  borderRadius: "50%",
                  background: "#e9ecef",
                  border: "2px solid #fff",
                  boxShadow: "0 0 0 1px rgba(0,0,0,0.05)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 36,
                  fontWeight: 600,
                  color: "#6c757d",
                }}
                aria-label="Learner image placeholder"
              >
                {(
                  pendingLearner?.user?.first_name?.[0] ||
                  pendingLearner?.user?.name?.[0] ||
                  "?"
                ).toUpperCase?.() ?? "?"}
              </div>
            </Col>
            <Col>
              <h4 className="mb-2">
                {pendingLearner?.user?.name ??
                  `${pendingLearner?.user?.first_name ?? ""} ${pendingLearner?.user?.last_name ?? ""}`.trim()}
              </h4>
              <div className="mt-3">
                <Row>
                  {confirmFields.map((f) => (
                    <Col key={f.label} xs={12} className="mb-2">
                      <strong>{f.label}:</strong> {f.value}
                    </Col>
                  ))}
                </Row>
              </div>
            </Col>
          </Row>
        </Container>
      </CommonModal>

      <CommonModal
        show={showAlert.userNotFound}
        onHide={() => hideAlert("userNotFound")}
        title={<Modal.Title>Alert</Modal.Title>}
        footer={
          <Button variant="dark" onClick={() => hideAlert("userNotFound")} className="px-4">
            Ok
          </Button>
        }
        modalProps={{
          centered: true,
          backdrop: true,
          className: "manage-roster-modal",
        }}
      >
        User not found.
      </CommonModal>
    </>
  );
};

export default ManageOfferingActivityRoster;
